/**
 * exp, log, pow and cos that give the same answer on every JavaScript engine.
 * The language lets each engine compute Math.exp and friends its own way, and they differ in the last digit between
 * versions (the online server and a browser did, and an online season drifted apart). These use only + − × ÷ on
 * numbers written out in full, which every engine must round the same way, so the browser, the server and a replay
 * agree to the bit. Accuracy is within a few units in the last place: plenty for a game.
 */

const LN2_HI = 6.93147180369123816490e-1;
const LN2_LO = 1.90821492927058770002e-10;
const INV_LN2 = 1.4426950408889634;
const LN10 = 2.302585092994046;
const TWO_PI_HI = 6.283185307179586;
const TWO_PI_LO = 2.4492935982947064e-16;

/** Exact powers of two from 2^-1074 to 2^1023, built by doubling and halving (both exact). */
const POW2 = new Float64Array(2098);
{
  let p = 1;
  for (let k = 0; k <= 1023; k++, p *= 2) POW2[1074 + k] = p;
  p = 1;
  for (let k = 0; k >= -1074; k--, p /= 2) POW2[1074 + k] = p;
}
const pow2 = (k: number): number => (k > 1023 ? Infinity : k < -1074 ? 0 : POW2[1074 + k]);

export function dexp(x: number): number {
  if (x !== x) return NaN;
  if (x > 709.78) return Infinity;
  if (x < -745.2) return 0;
  // x = k ln2 + r, |r| <= ln2 / 2
  const k = Math.round(x * INV_LN2);
  const r = x - k * LN2_HI - k * LN2_LO;
  // e^r to degree 14 (Horner, written-out 1/n!): error far below one unit in the last place for |r| < 0.35
  const s =
    1 + r * (1 + r * (0.5 + r * (0.16666666666666666 + r * (0.041666666666666664 + r * (0.008333333333333333 + r * (0.001388888888888889 +
    r * (0.0001984126984126984 + r * (0.0000248015873015873 + r * (0.0000027557319223985893 + r * (2.755731922398589e-7 +
    r * (2.505210838544172e-8 + r * (2.08767569878681e-9 + r * (1.6059043836821613e-10 + r * 1.1470745597729725e-11)))))))))))));
  // split the scaling near the ends of the range so it doesn't overflow or underflow early
  if (k > 1000 || k < -1000) {
    const h = Math.trunc(k / 2);
    return s * pow2(h) * pow2(k - h);
  }
  return s * pow2(k);
}

const bits = new DataView(new ArrayBuffer(8));

export function dlog(x: number): number {
  if (x !== x || x < 0) return NaN;
  if (x === 0) return -Infinity;
  if (x === Infinity) return Infinity;
  let y = x;
  let e = 0;
  if (y < 2.2250738585072014e-308) {
    y *= 18014398509481984; // 2^54: lift subnormals into the normal range
    e = -54;
  }
  // y = m 2^k with m in [1, 2): k read from the number's exponent bits (big-endian view: the same on every machine)
  bits.setFloat64(0, y);
  const k = ((bits.getUint32(0) >>> 20) & 0x7ff) - 1023;
  let m = y * pow2(-k);
  e += k;
  // into [sqrt(1/2), sqrt(2))
  if (m >= 1.4142135623730951) {
    m /= 2;
    e += 1;
  }
  // log m = 2 atanh(s), s = (m - 1) / (m + 1), |s| < 0.172: odd series to s^27
  const s = (m - 1) / (m + 1);
  const z = s * s;
  const t =
    1 + z * (0.3333333333333333 + z * (0.2 + z * (0.14285714285714285 + z * (0.1111111111111111 + z * (0.09090909090909091 +
    z * (0.07692307692307693 + z * (0.06666666666666667 + z * (0.058823529411764705 + z * (0.05263157894736842 +
    z * (0.047619047619047616 + z * (0.043478260869565216 + z * (0.04 + z * 0.037037037037037035))))))))))));
  return e * LN2_HI + (e * LN2_LO + 2 * s * t);
}

export function dlog10(x: number): number {
  return dlog(x) / LN10;
}

/** x^y. Whole-number powers multiply exactly; others go through exp and log. */
export function dpow(x: number, y: number): number {
  if (y === 0) return 1;
  if (x !== x || y !== y) return NaN;
  if (Number.isInteger(y) && Math.abs(y) <= 64) {
    let p = 1;
    let b = x;
    let n = Math.abs(y);
    while (n > 0) {
      if (n % 2 === 1) p *= b;
      n = Math.floor(n / 2);
      if (n > 0) b *= b;
    }
    return y < 0 ? 1 / p : p;
  }
  if (x === 0) return y > 0 ? 0 : Infinity;
  if (x < 0) return Number.isInteger(y) ? (Math.abs(y) % 2 === 1 ? -1 : 1) * dexp(y * dlog(-x)) : NaN;
  return dexp(y * dlog(x));
}

export function dcos(x: number): number {
  if (x !== x || !Number.isFinite(x)) return NaN;
  // into [-pi, pi]
  const k = Math.round(x / TWO_PI_HI);
  const r = x - k * TWO_PI_HI - k * TWO_PI_LO;
  // cos r to r^36 (Horner, written-out 1/(2n)!): covers |r| <= pi
  const z = r * r;
  return 1 + z * (-0.5 + z * (0.041666666666666664 + z * (-0.001388888888888889 + z * (0.0000248015873015873 + z * (-2.755731922398589e-7 +
    z * (2.08767569878681e-9 + z * (-1.1470745597729725e-11 + z * (4.779477332387385e-14 + z * (-1.5619206968586225e-16 +
    z * (4.110317623312165e-19 + z * (-8.896791392450574e-22 + z * (1.6117375710961184e-24 + z * (-2.4795962632247976e-27 +
    z * (3.279889237069838e-30 + z * (-3.7699876288159054e-33 + z * (3.8003907548547434e-36 + z * -3.3871575354676863e-39))))))))))))))));
}
